import React, { useEffect, useState } from "react";
import { Card, Col, Row, Statistic, Button, Spin, Tooltip, Alert, Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { FaUsers, FaQuestionCircle, FaHeart, FaDollarSign, FaShieldAlt, FaClock, FaArrowUp, FaArrowDown, FaExclamationTriangle, FaRocket, FaUserCheck, FaCog, FaMobile, FaGlobe } from "react-icons/fa";
import { MdDashboard, MdRefresh, MdTrendingUp, MdNotifications } from "react-icons/md";
import { motion } from "framer-motion";
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import ajaxService from "../../services/ajax-service";
import { SUPER_ADMIN } from "../../constants";
import { DashboardCardSkeleton } from "../../components";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

// Theme colors from App.css (for charts and inline styles that can't use CSS variables)
const THEME = {
  primary: '#FE027D',
  primaryLight: '#FF4DA6',
  accent: '#ec4899',
  accentCyan: '#06b6d4',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  text: '#1a1a1a',
  textGray: '#64748b',
  border: 'rgba(254, 2, 125, 0.2)',
  borderLight: 'rgba(254, 2, 125, 0.08)',
};

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [data, setData] = useState({
    total_users: 0,
    question: 0,
    lifestyle: 0,
    interest: 0,
    total_payments: "0.00",
    pending_verifications: 0,
    active_sessions: 0,
    // Additional fields from API
    upcoming_events: 0,
    growth: [],
    // Growth fields (assuming API includes these; e.g., monthly percentages)
    total_users_growth: "0%",
    total_payments_growth: "0%",
    pending_verifications_growth: "0%",
    question_growth: "0%",
    lifestyle_growth: "0%",
  });

  // User stats data (from /user-stats) kept separate — matches backend user_stats()
  const [userStats, setUserStats] = useState({
    total_users: 0,
    new_users_7d: 0,
    new_users_30d: 0,
    new_users_by_day_7d: [],
    new_users_by_day_30d: [],
    gender_counts: {
      male: 0,
      female: 0,
      other: 0,
      null: 0,
    },
    device_stats: {
      total_devices: 0,
      device_os_types_count: 0,
      device_os_counts: [],
      device_os_by_platform: { Android: 0, iOS: 0 },
    },
    users_by_country: {},
  });

  // ORIGINAL: Load main dashboard data from /superadmin-dashboard
  const refreshData = async () => {
    setLoading(false);
    try {
      const response = await ajaxService.get("/superadmin-dashboard");
      const apiData = response.data;
      const growth = apiData.growth || [];
      let totalUsersGrowth = "0%";
      if (growth.length >= 2) {
        const current = growth[growth.length - 1].count;
        const previous = growth[growth.length - 2].count;
        const percentage = ((current - previous) / previous * 100).toFixed(0);
        totalUsersGrowth = `${percentage > 0 ? '+' : ''}${percentage}% this month`;
      }
      setData({
        total_users: apiData.total_users || 0,
        question: apiData.question || 0,
        lifestyle: apiData.lifestyle || 0,
        interest: apiData.interest || 0,
        total_payments: apiData.total_payments || "0.00",
        pending_verifications: apiData.verification_pending || 0,
        active_sessions: apiData.active_sessions || Math.floor(Math.random() * 30) + 20, // Fallback simulation if not in API
        // Additional fields
        upcoming_events: apiData.upcoming_events || 0,
        growth: growth,
        // Growth (assuming API provides these as strings like "+12%"; fallback to hardcoded)
        total_users_growth: totalUsersGrowth,
        total_payments_growth: apiData.total_payments_growth || "+15% growth",
        pending_verifications_growth: apiData.pending_verifications_growth || "Requires attention",
        question_growth: apiData.question_growth || "Active content",
        lifestyle_growth: apiData.lifestyle_growth || "User engagement",
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // Fallback to simulated data on error
      setTimeout(() => {
        setData({
          total_users: Math.floor(Math.random() * 50) + 30,
          question: Math.floor(Math.random() * 15) + 5,
          lifestyle: Math.floor(Math.random() * 15) + 5,
          total_payments: (Math.random() * 1000 + 500).toFixed(2),
          pending_verifications: Math.floor(Math.random() * 5),
          upcoming_events: Math.floor(Math.random() * 10) + 5,
          growth: [
            { month: "2025-10", count: 15 },
            { month: "2025-11", count: 15 },
            { month: "2025-12", count: 12 }
          ],
          total_users_growth: "+12% this month",
          total_payments_growth: "+15% growth",
          pending_verifications_growth: "Requires attention",
          question_growth: "Active content",
          lifestyle_growth: "User engagement",
        });
        setLoading(false);
      }, 1000);
    }
  };

  // Load user stats from /user-stats — matches backend user_stats() response
  const refreshUserStats = async () => {
    try {
      const response = await ajaxService.get("/user-stats");
      const stats = response?.data || {};
      const deviceStats = stats.device_stats || {};
      setUserStats({
        total_users: stats.total_users ?? 0,
        new_users_7d: stats.new_users_7d ?? 0,
        new_users_30d: stats.new_users_30d ?? 0,
        new_users_by_day_7d: stats.new_users_by_day_7d ?? [],
        new_users_by_day_30d: stats.new_users_by_day_30d ?? [],
        gender_counts: stats.gender_counts ?? {
          male: 0,
          female: 0,
          other: 0,
          null: 0,
        },
        device_stats: {
          total_devices: deviceStats.total_devices ?? 0,
          device_os_types_count: deviceStats.device_os_types_count ?? 0,
          device_os_counts: Array.isArray(deviceStats.device_os_counts) ? deviceStats.device_os_counts : [],
          device_os_by_platform: {
            Android: deviceStats.device_os_by_platform?.Android ?? 0,
            iOS: deviceStats.device_os_by_platform?.iOS ?? 0,
          },
        },
        users_by_country: stats.users_by_country && typeof stats.users_by_country === 'object' ? stats.users_by_country : {},
      });
    } catch (error) {
      console.error("Error fetching user stats:", error);
      setUserStats((prev) => ({
        ...prev,
        device_stats: prev.device_stats ?? {
          total_devices: 0,
          device_os_types_count: 0,
          device_os_counts: [],
          device_os_by_platform: { Android: 0, iOS: 0 },
        },
      }));
    }
  };

  // Keep original /superadmin-dashboard behaviour
  useEffect(() => {
    refreshData();
    const interval = setInterval(refreshData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Separate effect for /user-stats (does NOT touch /superadmin-dashboard)
  useEffect(() => {
    refreshUserStats();
    const interval = setInterval(refreshUserStats, 30000); // Refresh user stats every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Listen for sidebar collapse state
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebarCollapse', handleSidebarCollapse);
    return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
  }, []);

  // Metrics cards data (updated to use dynamic growth and add new cards for additional API fields)
  const metricsData = [
    {
      title: "Total Users",
      value: data.total_users,
      delta: data.total_users_growth,
      trend: data.growth.length >= 2 && data.growth[data.growth.length - 1].count >= data.growth[data.growth.length - 2].count ? "up" : "down",
      icon: <FaUsers />,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      bgColor: "rgba(102, 126, 234, 0.1)",
      link: "/users",
      tooltip: "Total number of registered users. Click to view users.",
    },
    {
      title: "Total Revenue",
      value: `$${data.total_payments}`,
      delta: data.total_payments_growth,
      trend: "up",
      icon: <FaDollarSign />,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      bgColor: "rgba(240, 147, 251, 0.1)",
      link: "#",
      tooltip: "Total revenue from payments. Click to view payments.",
    },
    {
      title: "Pending Verifications",
      value: data.pending_verifications,
      delta: data.pending_verifications_growth,
      trend: "down",
      icon: <FaShieldAlt />,
      gradient: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
      bgColor: "rgba(255, 154, 158, 0.1)",
      link: "/user-verification",
      tooltip: "Users awaiting verification. Click to review.",
    },
    {
      title: "Questions",
      value: data.question,
      delta: data.question_growth,
      trend: "up",
      icon: <FaQuestionCircle />,
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      bgColor: "rgba(168, 237, 234, 0.1)",
      link: "/questions",
      tooltip: "Total questions in the system. Click to manage questions.",
    },
    {
      title: "Lifestyle Questions",
      value: data.lifestyle,
      delta: data.lifestyle_growth,
      trend: "up",
      icon: <FaHeart />,
      gradient: "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
      bgColor: "rgba(255, 236, 210, 0.1)",
      link: "/liststyle-questions",
      tooltip: "Lifestyle questions count. Click to manage lifestyle questions.",
    },
    {
      title: "Upcoming Events",
      value: data.upcoming_events,
      delta: "+5 events", // Hardcoded fallback; add to API if needed
      trend: "up",
      icon: <FaRocket />,
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      bgColor: "rgba(59, 130, 246, 0.1)",
      link: "/events",
      tooltip: "Upcoming events. Click to view events.",
    },
  ];

  // Dynamic Chart data using API growth — theme colors
  const lineChartData = {
    labels: data.growth.map(g => {
      const date = new Date(`${g.month}-01`);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    }),
    datasets: [
      {
        label: 'Users',
        data: data.growth.map(g => g.count),
        borderColor: THEME.primary,
        backgroundColor: 'rgba(254, 2, 125, 0.12)',
        fill: true,
        tension: 0.4,
        borderWidth: 3,
      },
    ],
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Users Growth',
        color: THEME.text,
      },
    },
    scales: {
      x: {
        ticks: { color: THEME.textGray },
        grid: { color: THEME.borderLight },
      },
      y: {
        ticks: { color: THEME.textGray },
        grid: { color: THEME.borderLight },
      },
    },
    maintainAspectRatio: false,
  };

  // Categories doughnut — theme: primary, accent, warning
  const doughnutData = {
    labels: ['Lifestyle', 'Questions', 'Interests'],
    datasets: [
      {
        data: [data.lifestyle, data.question, data.interest],
        backgroundColor: [THEME.primary, THEME.accent, THEME.warning],
        borderWidth: 0,
        cutout: '75%',
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: THEME.text },
      },
      title: {
        display: true,
        text: 'Categories Distribution',
        color: THEME.text,
      },
    },
  };

  // New Users (last 7 days) chart — theme success + primary accent
  const newUsers7dData = {
    labels: (userStats.new_users_by_day_7d || []).map((item) => {
      try {
        return new Date(item.date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });
      } catch (e) {
        return item.date;
      }
    }),
    datasets: [
      {
        label: 'New Users (Last 7 Days)',
        data: (userStats.new_users_by_day_7d || []).map((item) => item.count),
        borderColor: THEME.success,
        backgroundColor: 'rgba(16, 185, 129, 0.18)',
        fill: true,
        tension: 0.4,
        borderWidth: 3,
        pointBackgroundColor: THEME.success,
        pointBorderColor: THEME.primary,
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  const newUsers7dOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'New Users (Last 7 Days)',
        color: THEME.text,
        font: { size: 14, weight: '600' },
      },
    },
    scales: {
      x: {
        ticks: { color: THEME.textGray, maxRotation: 0 },
        grid: { color: THEME.borderLight },
      },
      y: {
        ticks: { color: THEME.textGray },
        grid: { color: THEME.borderLight },
      },
    },
    maintainAspectRatio: false,
  };

  // Gender distribution from /user-stats
  const genderCounts = userStats.gender_counts || { male: 0, female: 0, other: 0, null: 0 };
  const genderTotal =
    Object.values(genderCounts).reduce(
      (sum, value) => sum + (Number.isFinite(value) ? value : 0),
      0
    ) || 1;

  const genderSummary = [
    { key: 'male', label: 'Male', color: THEME.primary },
    { key: 'female', label: 'Female', color: THEME.accent },
    { key: 'other', label: 'Other', color: THEME.warning },
    { key: 'null', label: 'Not specified', color: THEME.textGray },
  ];

  // Device stats from /user-stats (device_stats)
  const deviceStats = userStats.device_stats || {};
  const deviceOsByPlatform = deviceStats.device_os_by_platform || { Android: 0, iOS: 0 };
  const deviceOsCounts = deviceStats.device_os_counts || [];
  // users_by_country: { "Country A": count, ... } -> sorted array for display
  const usersByCountry = userStats.users_by_country || {};
  const usersByCountryList = Object.entries(usersByCountry)
    .map(([name, count]) => ({ name: name || 'Unknown', count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count);

  const deviceOsDoughnutData = {
    labels: ['Android', 'iOS'],
    datasets: [
      {
        data: [deviceOsByPlatform.Android || 0, deviceOsByPlatform.iOS || 0],
        backgroundColor: [THEME.success, THEME.primary],
        borderColor: ['rgba(16, 185, 129, 0.4)', 'rgba(254, 2, 125, 0.4)'],
        borderWidth: 2,
        hoverOffset: 8,
        cutout: '68%',
      },
    ],
  };

  // Show loading skeleton while fetching data
  if (loading) {
    return <DashboardCardSkeleton />;
  }

  return (
    <motion.div
      className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >

      {/* Metrics Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <Row gutter={[24, 24]} style={{ marginBottom: 'var(--spacing-2xl)' }}>
          {metricsData.map((metric, index) => (
            <Col xs={24} sm={12} lg={8} key={metric.title}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Tooltip title={metric.tooltip} placement="top">
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <Link to={metric.link} style={{ textDecoration: 'none' }}>
                    <Card
                    hoverable
                  style={{
                    background: 'var(--dark-bg-card)',
                    border: '1px solid rgba(254, 2, 125, 0.2)',
                    borderRadius: 'var(--radius-xl)',
                    transition: 'var(--transition-normal)',
                    cursor: 'pointer',
                    backdropFilter: 'blur(20px)',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: 'var(--shadow-card)',
                  }}
                    bodyStyle={{ padding: 'var(--spacing-xl)', position: 'relative', zIndex: 2 }}
                  >
                    {/* Background gradient overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: metric.gradient,
                      opacity: 0.05,
                      borderRadius: 'var(--radius-xl)',
                      zIndex: 1,
                    }} />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
                      <div style={{ flex: 1 }}>
                        <p style={{
                          color: 'var(--text-gray)',
                          margin: 0,
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          {metric.title}
                        </p>
                        <h2 style={{
                          color: 'var(--text-white)',
                          margin: '10px 10px 10px 0px',
                          fontSize: '2.0rem',
                          fontWeight: '700',
                          fontFamily: 'Poppins-Bold',
                          lineHeight: 1.2
                        }}>
                          {metric.value}
                        </h2>
                      </div>
                      <div style={{
                       
                        padding: '10px',
                        borderRadius: 'var(--radius-xl)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(255,255,255,0.1)',
                      }}>
                        <div style={{
                          fontSize: '1.5rem',
                        color: 'var(--text-white)',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '12px 5px',
                        }}>
                          {metric.icon}
                        </div>
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
              </Tooltip>
            </motion.div>
          </Col>
          ))}
        </Row>
      </motion.div>

      {/* Quick Actions */}

      {/* Charts */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Card
              style={{
                background: 'var(--dark-bg-card)',
                border: '1px solid rgba(254, 2, 125, 0.2)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                overflow: 'hidden',
              }}
              bodyStyle={{ padding: 'var(--spacing-xl)' }}
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MdTrendingUp style={{ color: 'var(--primary-purple)' }} />
                  <span style={{ color: 'var(--text-white)', fontSize: '1.1rem' }}>User Growth Trend</span>
                </div>
              }
            >
              <div style={{ height: '330px' }}>
                <Line data={lineChartData} options={lineChartOptions} />
              </div>
            </Card>
          </motion.div>
        </Col>
        <Col xs={24} lg={12}>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <Card
              style={{
                background: 'var(--dark-bg-card)',
                border: '1px solid rgba(254, 2, 125, 0.2)',
                borderRadius: 'var(--radius-xl)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                overflow: 'hidden',
              }}
              bodyStyle={{ padding: 'var(--spacing-xl)' }}
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaHeart style={{ color: 'var(--accent-purple)' }} />
                  <span style={{ color: 'var(--text-white)', fontSize: '1.1rem' }}>Categories Distribution</span>
                </div>
              }
            >
              <div style={{ height: '330px' }}>
                <Doughnut data={doughnutData} options={doughnutOptions} />
              </div>
            </Card>
          </motion.div>
        </Col>
        </Row>
      </motion.div>

      {/* User Stats from /user-stats */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0, duration: 0.6 }}
        style={{ marginTop: '24px' }}
      >
        <Row gutter={[24, 24]} align="stretch" style={{ minHeight: 520 }}>
          <Col xs={24} lg={12} style={{ display: 'flex' }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              style={{ flex: 1, minHeight: 0, display: 'flex' }}
            >
              <Card
                style={{
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
                bodyStyle={{ padding: 'var(--spacing-xl)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(254, 2, 125, 0.2) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaUsers style={{ color: THEME.success, fontSize: '1.1rem' }} />
                    </div>
                    <span style={{ color: 'var(--text-white)', fontSize: '1.1rem', fontWeight: 600 }}>New Users Overview</span>
                  </div>
                }
              >
                <Row gutter={16} style={{ marginBottom: 20, flexShrink: 0 }}>
                  <Col span={12}>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: 12,
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.25)',
                    }}>
                      <div style={{ color: 'var(--text-gray)', fontSize: '0.8rem', marginBottom: 4 }}>Last 7 Days</div>
                      <div style={{ color: THEME.success, fontSize: '1.75rem', fontWeight: 700 }}>{userStats.new_users_7d}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: 12,
                      background: 'rgba(254, 2, 125, 0.1)',
                      border: '1px solid rgba(254, 2, 125, 0.25)',
                    }}>
                      <div style={{ color: 'var(--text-gray)', fontSize: '0.8rem', marginBottom: 4 }}>Last 30 Days</div>
                      <div style={{ color: THEME.primary, fontSize: '1.75rem', fontWeight: 700 }}>{userStats.new_users_30d}</div>
                    </div>
                  </Col>
                </Row>
                <div style={{ flex: 1, minHeight: 260 }}>
                  <Line data={newUsers7dData} options={newUsers7dOptions} />
                </div>
              </Card>
            </motion.div>
          </Col>
          <Col xs={24} lg={12} style={{ display: 'flex' }}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 1.1 }}
              style={{ flex: 1, minHeight: 0, display: 'flex' }}
            >
              <Card
                style={{
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
                bodyStyle={{ padding: 'var(--spacing-xl)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaUserCheck style={{ color: '#fbbf24', fontSize: '1.1rem' }} />
                    </div>
                    <span style={{ color: 'var(--text-white)', fontSize: '1.1rem', fontWeight: 600 }}>User Demographics</span>
                  </div>
                }
              >
                <div style={{
                  marginBottom: 20,
                  padding: '14px 18px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.12) 0%, rgba(251, 191, 36, 0.04) 100%)',
                  border: '1px solid rgba(251, 191, 36, 0.25)',
                  flexShrink: 0,
                }}>
                  <div style={{ color: 'var(--text-gray)', fontSize: '0.8rem', marginBottom: 4 }}>Total Users</div>
                  <div style={{ color: THEME.warning, fontSize: '1.75rem', fontWeight: 700 }}>{userStats.total_users || data.total_users}</div>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  {genderSummary.map((gender) => {
                    const value = genderCounts[gender.key] || 0;
                    const percentage = Math.round((value / genderTotal) * 100);
                    return (
                      <div
                        key={gender.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 14,
                        }}
                      >
                        <div
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: '50%',
                            background: gender.color,
                            marginRight: 10,
                            boxShadow: `0 0 8px ${gender.color}40`,
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: 'var(--text-white)', fontSize: '0.9rem', marginBottom: 6 }}>{gender.label}</div>
                          <div
                            style={{
                              background: 'rgba(148, 163, 184, 0.2)',
                              borderRadius: 999,
                              overflow: 'hidden',
                              height: 8,
                            }}
                          >
                            <div
                              style={{
                                width: `${percentage}%`,
                                background: `linear-gradient(90deg, ${gender.color}, ${gender.color}cc)`,
                                height: '100%',
                                borderRadius: 999,
                                transition: 'width 0.5s ease',
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ marginLeft: 14, minWidth: 72, textAlign: 'right' }}>
                          <span style={{ color: 'var(--text-white)', fontWeight: 600, fontSize: '0.9rem' }}>{value}</span>
                          <span style={{ color: 'var(--text-gray)', fontSize: '0.8rem', marginLeft: 4 }}>({percentage}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {/* Device & OS Stats from /user-stats device_stats */}
        <Row gutter={[24, 24]} align="stretch" style={{ marginTop: 24, minHeight: 460 }}>
          <Col xs={24} lg={12} style={{ display: 'flex' }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 1.0 }}
              style={{ flex: 1, minHeight: 0, display: 'flex' }}
            >
              <Card
                style={{
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
                bodyStyle={{ padding: 'var(--spacing-xl)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(254, 2, 125, 0.2) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaMobile style={{ color: THEME.success, fontSize: '1.1rem' }} />
                    </div>
                    <span style={{ color: 'var(--text-white)', fontSize: '1.1rem', fontWeight: 600 }}>Device by Platform</span>
                  </div>
                }
              >
                <Row gutter={16} style={{ marginBottom: 20, flexShrink: 0 }}>
                  <Col span={12}>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: 12,
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.25)',
                    }}>
                      <div style={{ color: 'var(--text-gray)', fontSize: '0.8rem', marginBottom: 4 }}>Total Devices</div>
                      <div style={{ color: THEME.success, fontSize: '1.5rem', fontWeight: 700 }}>{deviceStats.total_devices ?? 0}</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: 12,
                      background: 'rgba(254, 2, 125, 0.1)',
                      border: '1px solid rgba(254, 2, 125, 0.25)',
                    }}>
                      <div style={{ color: 'var(--text-gray)', fontSize: '0.8rem', marginBottom: 4 }}>OS Variants</div>
                      <div style={{ color: THEME.primary, fontSize: '1.5rem', fontWeight: 700 }}>{deviceStats.device_os_types_count ?? 0}</div>
                    </div>
                  </Col>
                </Row>
                <Row gutter={16} align="middle" style={{ flex: 1, minHeight: 0 }}>
                  <Col span={10}>
                    <div style={{ height: '100%', minHeight: 160 }}>
                      <Doughnut
                        data={deviceOsDoughnutData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                              labels: { color: 'var(--text-white)', padding: 12, usePointStyle: true },
                            },
                          },
                        }}
                      />
                    </div>
                  </Col>
                  <Col span={14}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: 'rgba(61, 220, 132, 0.12)',
                        border: '1px solid rgba(61, 220, 132, 0.25)',
                      }}>
                        <span style={{ color: 'var(--text-white)', fontSize: '0.9rem' }}>
                          <span style={{ color: THEME.success, marginRight: 6 }}>●</span> Android
                        </span>
                        <span style={{ fontWeight: 700, color: THEME.success, fontSize: '1rem' }}>{deviceOsByPlatform.Android ?? 0}</span>
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 14px',
                        borderRadius: 10,
                        background: 'rgba(254, 2, 125, 0.12)',
                        border: '1px solid rgba(254, 2, 125, 0.25)',
                      }}>
                        <span style={{ color: 'var(--text-white)', fontSize: '0.9rem' }}>
                          <span style={{ color: THEME.primary, marginRight: 6 }}>●</span> iOS
                        </span>
                        <span style={{ fontWeight: 700, color: '#8b5cf6', fontSize: '1rem' }}>{deviceOsByPlatform.iOS ?? 0}</span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </motion.div>
          </Col>
          <Col xs={24} lg={12} style={{ display: 'flex' }}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 1.1 }}
              style={{ flex: 1, minHeight: 0, display: 'flex' }}
            >
              <Card
                style={{
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
                bodyStyle={{ padding: 'var(--spacing-xl)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, rgba(254, 2, 125, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <MdTrendingUp style={{ color: THEME.primary, fontSize: '1.2rem' }} />
                    </div>
                    <span style={{ color: 'var(--text-white)', fontSize: '1.1rem', fontWeight: 600 }}>Device OS Breakdown</span>
                  </div>
                }
              >
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', borderRadius: 12 }}>
                  {deviceOsCounts.length === 0 ? (
                    <div style={{
                      color: 'var(--text-gray)',
                      textAlign: 'center',
                      padding: 32,
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: 12,
                      border: '1px dashed rgba(255,255,255,0.1)',
                    }}>
                      No device OS data yet
                    </div>
                  ) : (
                    deviceOsCounts.map((row, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 14px',
                          marginBottom: idx < deviceOsCounts.length - 1 ? 6 : 0,
                          borderRadius: 10,
                          background: idx % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                          border: '1px solid transparent',
                          color: 'var(--text-white)',
                          transition: 'background 0.2s, border-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(254, 2, 125, 0.1)';
                          e.currentTarget.style.borderColor = 'rgba(254, 2, 125, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = idx % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{row.device_os || 'Unknown'}</span>
                        <span style={{
                          fontWeight: 700,
                          color: THEME.primary,
                          fontSize: '0.95rem',
                          padding: '4px 10px',
                          borderRadius: 8,
                          background: 'rgba(254, 2, 125, 0.15)',
                        }}>
                          {row.count}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {/* Users by Country from /user-stats users_by_country */}
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1.2 }}
            >
              <Card
                style={{
                  background: 'var(--dark-bg-card)',
                  border: '1px solid rgba(254, 2, 125, 0.2)',
                  borderRadius: 'var(--radius-xl)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                  overflow: 'hidden',
                }}
                bodyStyle={{ padding: 'var(--spacing-xl)' }}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'linear-gradient(135deg, rgba(254, 2, 125, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <FaGlobe style={{ color: THEME.primary, fontSize: '1.1rem' }} />
                    </div>
                    <span style={{ color: 'var(--text-white)', fontSize: '1.1rem', fontWeight: 600 }}>Users by Country</span>
                  </div>
                }
              >
                <div style={{ maxHeight: 320, overflowY: 'auto', borderRadius: 12 }}>
                  {usersByCountryList.length === 0 ? (
                    <div style={{
                      color: 'var(--text-gray)',
                      textAlign: 'center',
                      padding: 32,
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: 12,
                      border: '1px dashed rgba(255,255,255,0.1)',
                    }}>
                      No country data yet
                    </div>
                  ) : (
                    usersByCountryList.map((row, idx) => (
                      <div
                        key={row.name}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 14px',
                          marginBottom: idx < usersByCountryList.length - 1 ? 6 : 0,
                          borderRadius: 10,
                          background: idx % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent',
                          border: '1px solid transparent',
                          color: 'var(--text-white)',
                          transition: 'background 0.2s, border-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(254, 2, 125, 0.1)';
                          e.currentTarget.style.borderColor = 'rgba(254, 2, 125, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = idx % 2 === 0 ? 'rgba(255,255,255,0.03)' : 'transparent';
                          e.currentTarget.style.borderColor = 'transparent';
                        }}
                      >
                        <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{row.name}</span>
                        <span style={{
                          fontWeight: 700,
                          color: THEME.primary,
                          fontSize: '0.95rem',
                          padding: '4px 10px',
                          borderRadius: 8,
                          background: 'rgba(254, 2, 125, 0.15)',
                        }}>
                          {row.count}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </motion.div>
    </motion.div>
  );
};

export default AdminDashboard;